import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { areIdsEqual } from '../utils/typeUtils';
import { getErrorMessage, logError } from '../utils/errorUtils';
import { LoadingState, ErrorDisplay } from '../components/common';
import { ConfirmationDialog } from '../components/modals';
import { ResourceDetailsCard } from '../components/resources';
import { UserDetailsCard } from '../components/users';
import { ReservationDetailsCard } from '../components/reservations';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider
} from '@mui/material';
import { reservationsApi, resourcesApi, usersApi } from '../services/api';
import { Reservation, Resource, User } from '../types';

const ReservationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [resource, setResource] = useState<Resource | null>(null);
  const [reservationUser, setReservationUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    const fetchReservationDetails = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const reservationData = await reservationsApi.getById(id);
        setReservation(reservationData);
        
        // Fetch resource and user details
        if (reservationData.resourceId) {
          const resourceData = await resourcesApi.getById(reservationData.resourceId);
          setResource(resourceData);
        }
        
        if (reservationData.userId) {
          const userData = await usersApi.getById(reservationData.userId);
          setReservationUser(userData);
        }
      } catch (err) {
        logError(err, 'fetching reservation details');
        setError(getErrorMessage(err, 'Failed to load reservation details. Please try again later.'));
      } finally {
        setLoading(false);
      }
    };

    fetchReservationDetails();
  }, [id]);

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!id) {
      setError('No reservation ID available');
      return;
    }
    
    if (!reservation) {
      setError('No reservation data available');
      return;
    }
    
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      await reservationsApi.cancel(id);
      navigate('/reservations');
    } catch (err) {
      logError(err, 'cancelling reservation');
      setError(getErrorMessage(err, 'Failed to cancel reservation. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };

  // No longer needed as we're using ReservationDetailsCard

  if (loading) {
    return <LoadingState message="Loading reservation details..." />;
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={() => window.location.reload()} />;
  }

  if (!reservation) {
    return <Typography>Reservation not found.</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Reservation Details</Typography>
        <Box>
          <Button 
            variant="outlined" 
            color="primary"
            component={Link}
            to="/reservations"
            sx={{ mr: 2 }}
          >
            Back to Reservations
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleOpenConfirmDialog}
            disabled={currentUser?.role !== 'admin' && !areIdsEqual(reservation.userId, currentUser?.id)}
            title={currentUser?.role !== 'admin' && !areIdsEqual(reservation.userId, currentUser?.id) ? 'You can only cancel your own reservations' : ''}
          >
            Cancel Reservation
          </Button>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Box>
            <ReservationDetailsCard reservation={reservation} elevation={0} />  
          </Box>
          
          <Box>
            {resource ? (
              <ResourceDetailsCard resource={resource} elevation={0} />
            ) : (
              <Typography>Resource information not available</Typography>
            )}
          </Box>

          <Box>
            <Divider sx={{ my: 2 }} />

            {reservationUser ? (
              <UserDetailsCard user={reservationUser} elevation={0} />
            ) : (
              <Typography>User information not available</Typography>
            )}
          </Box>
        </Box>
      </Paper>

      <ConfirmationDialog
        open={confirmDialogOpen}
        title="Cancel Reservation"
        message="Are you sure you want to cancel this reservation? This action cannot be undone."
        confirmLabel="Cancel Reservation"
        cancelLabel="Keep Reservation"
        onConfirm={handleDelete}
        onCancel={handleCloseConfirmDialog}
        confirmColor="error"
      />
    </Box>
  );
};

export default ReservationDetails;
